import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getItem, createItem, updateItem } from "../services/firestore";

import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";

import { ArrowLeft, Save, Loader2, Package } from "lucide-react";
import { toast } from "sonner";

export const ItemFormPage = () => {

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();

  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    brand: "",
    category: "",
    hsn: "",
    uom: "",
    rate: "",
    gst_percent: "18"
  });

  const [errors, setErrors] = useState({});

  /* =========================
     FETCH ITEM (EDIT MODE)
  ========================= */

  useEffect(() => {
    if (isEdit) fetchItem();
  }, [id]);

  const fetchItem = async () => {
    try {

      const item = await getItem(id);

      if (!item) {
        toast.error("Item not found");
        navigate("/items");
        return;
      }

      setFormData({
        name: item.item_name || "",
        code: item.item_code || "",
        description: item.description || "",
        brand: item.brand || "",
        category: item.category || "",
        hsn: item.HSN || "",
        uom: item.UOM || "",
        rate: item.rate?.toString() || "",
        gst_percent: item.GST_percent?.toString() || "18"
      });

    } catch (error) {

      console.error(error);
      toast.error("Failed to load item");
      navigate("/items");

    } finally {

      setLoading(false);

    }
  };

  /* =========================
     COPY ITEM (NEW MODE)
  ========================= */

  useEffect(() => {

    if (location.state?.copyItem && !isEdit) {

      const item = location.state.copyItem;

      setFormData({
        name: item.item_name || "",
        code: "", // force new code
        description: item.description || "",
        brand: item.brand || "",
        category: item.category || "",
        hsn: item.HSN || "",
        uom: item.UOM || "",
        rate: item.rate?.toString() || "",
        gst_percent: item.GST_percent?.toString() || "18"
      });

    }

  }, [location.state, isEdit]);

  /* =========================
     VALIDATION
  ========================= */

  const validateForm = () => {

    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Item name is required";
    }

    if (!formData.uom.trim()) {
      newErrors.uom = "UOM is required";
    }

    if (formData.rate && isNaN(parseFloat(formData.rate))) {
      newErrors.rate = "Invalid rate";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /* =========================
     INPUT CHANGE
  ========================= */

  const handleChange = (e) => {

    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

  };

  /* =========================
     SEARCH INDEX BUILDER
  ========================= */

  const buildSearchIndex = () => {

    return [
      formData.name,
      formData.code,
      formData.brand,
      formData.category,
      formData.description
    ]
      .join(" ")
      .toLowerCase()
      .trim();

  };

  /* =========================
     SUBMIT
  ========================= */

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (!validateForm()) {
      toast.error("Fix errors before saving");
      return;
    }

    setSaving(true);

    try {

      const dataToSave = {

        item_name: formData.name.trim(),
        item_code: formData.code.trim(),

        description: formData.description.trim(),

        brand: formData.brand.trim(),
        category: formData.category.trim(),

        HSN: formData.hsn.trim(),
        UOM: formData.uom.trim().toUpperCase(),

        rate: parseFloat(formData.rate) || 0,
        GST_percent: parseFloat(formData.gst_percent) || 0,

        search_index: buildSearchIndex(),

        updated_at: new Date()
      };

      if (isEdit) {

        await updateItem(id, dataToSave);
        toast.success("Item updated successfully");

      } else {

        await createItem(dataToSave, userData?.uid);
        toast.success("Item created successfully");

      }

      navigate("/items");

    } catch (error) {

      console.error(error);
      toast.error("Failed to save item");

    } finally {

      setSaving(false);

    }

  };

  /* =========================
     LOADING
  ========================= */

  if (loading) {

    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-96" />
      </div>
    );

  }

  /* =========================
     UI
  ========================= */

  return (

    <div className="p-6 max-w-2xl mx-auto">

      {/* HEADER */}

      <div className="flex items-center gap-4 mb-6">

        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/items")}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <div>

          <h1 className="text-2xl font-bold">
            {isEdit ? "Edit Item" : "New Item"}
          </h1>

          <p className="text-slate-500 text-sm">
            Manage item details
          </p>

        </div>

      </div>

      {/* FORM */}

      <form onSubmit={handleSubmit}>

        <Card>

          <CardHeader>
            <CardTitle className="flex gap-2">
              <Package className="w-5 h-5 text-emerald-500" />
              Item Details
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">

            {/* NAME */}
            <div>
              <Label>Item Name *</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            {/* CODE + HSN */}
            <div className="grid grid-cols-2 gap-4">

              <div>
                <Label>Item Code</Label>
                <Input
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Label>HSN</Label>
                <Input
                  name="hsn"
                  value={formData.hsn}
                  onChange={handleChange}
                />
              </div>

            </div>

            {/* BRAND + CATEGORY */}
            <div className="grid grid-cols-2 gap-4">

              <div>
                <Label>Brand</Label>
                <Input
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  placeholder="e.g. AXITEC"
                />
              </div>

              <div>
                <Label>Category</Label>
                <Input
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  placeholder="e.g. Solar Panels"
                />
              </div>

            </div>

            {/* DESCRIPTION */}
            <div>
              <Label>Description</Label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </div>

            {/* UOM */}
            <div>
              <Label>UOM *</Label>
              <Input
                name="uom"
                value={formData.uom}
                onChange={handleChange}
              />
            </div>

            {/* RATE + GST */}
            <div className="grid grid-cols-2 gap-4">

              <div>
                <Label>Rate</Label>
                <Input
                  name="rate"
                  type="number"
                  value={formData.rate}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Label>GST %</Label>
                <Input
                  name="gst_percent"
                  type="number"
                  value={formData.gst_percent}
                  onChange={handleChange}
                />
              </div>

            </div>

          </CardContent>

        </Card>

        {/* ACTIONS */}
        <div className="flex gap-3 mt-6">

          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => navigate("/items")}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            className="flex-1 solar-gradient"
            disabled={saving}
          >

            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEdit ? "Update" : "Create"} Item
              </>
            )}

          </Button>

        </div>

      </form>

    </div>

  );

};

export default ItemFormPage;